import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  RadioGroup,
  Radio,
  InputLabel
} from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import users from '../../../apis/users';
import { Gender, SnackbarSeverityType } from '../../../enums';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { getNewStaffRoles } from '../config/constant';
import { StyledRadioButton } from '../../common/styled';
import pallet from '../../../layouts/pallet';
import { fontSizes } from '../../../layouts/fontSizes';

interface AddNewStaffFormProps {
  open: boolean;
  onClose: () => void;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}

const AddNewStaffForm: React.FC<AddNewStaffFormProps> = ({
  open,
  onClose,
  showToast
}) => {
  const [role, setRole] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  const [memberCode, setMemberCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const handleModalClose = () => {
    onClose();
    setRole('');
    setName('');
    setEmail('');
    setGender('');
    setMemberCode('');
  };

  const addNewStaffUser = async () => {
    try {
      const formData = {
        name,
        email,
        gender,
        role,
        school: loggedInUser.school,
        memberCode
      };
      await users.addNewStaffUser(formData);
      showToast('New staff added successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.error?.errors?.[0].message || 'Error adding new staff',
        SnackbarSeverityType.error
      );
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (!role || !name || !email || !gender) {
        setIsLoading(false);
        return;
      }
      await addNewStaffUser();
      handleModalClose();
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = role && name && email && gender;

  return (
    <DialogModal
      open={open}
      onClose={handleModalClose}
      title='Add Staff User'
      maxWidth='sm'
      actions={
        <>
          <Button
            onClick={handleModalClose}
            color='secondary'
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color='primary'
            variant='contained'
            disabled={isLoading || !isFormValid}
          >
            {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Add'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DropdownSelect
          label='Role'
          placeholder='Select Role'
          name='role'
          value={role}
          options={getNewStaffRoles(loggedInUser?.school)}
          onChange={(event) => setRole(event.target.value as string)}
          required
          fullWidth
          hasTopLabel
        />
        <TextInputWithIcon
          label='Name'
          placeholder='Enter Name'
          name='name'
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
        />
        <TextInputWithIcon
          label='Email'
          placeholder='Enter Email'
          name='email'
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
        />
        <TextInputWithIcon
          label='Employee Code'
          placeholder='Enter Employee Code'
          name='memberCode'
          value={memberCode}
          onChange={(e) => setMemberCode(e.target.value)}
          fullWidth
          required={false}
        />

        <>
          <InputLabel
            required
            sx={{
              color: pallet.textLabel,
              fontSize: `${fontSizes.placeHolder} !important`,
              fontWeight: 600
            }}
          >
            Gender
          </InputLabel>
          <RadioGroup
            row
            name='gender'
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            sx={{ gap: 2 }}
          >
            <StyledRadioButton
              value={Gender.MALE}
              control={<Radio />}
              label='Male'
            />
            <StyledRadioButton
              value={Gender.FEMALE}
              control={<Radio />}
              label='Female'
            />
          </RadioGroup>
        </>
      </Box>
    </DialogModal>
  );
};

export default AddNewStaffForm;
